import { formatDistanceToNow } from 'date-fns'

// Maximum value for a Unix timestamp in seconds. Used to distinguish between second-based and millisecond-based timestamps.
const TIMESTAMP_THRESHOLD = 9999999999

/**
 * Formats a given date string into a human-readable format.
 *
 * This function uses the `Intl.DateTimeFormat` API to format the date
 * in the 'en-US' locale with the following options:
 * - `timeStyle: 'long'`: Includes detailed time information (e.g., hours, minutes, seconds).
 * - `dateStyle: 'medium'`: Includes the month name, day, and year.
 * - `timeZone: 'UTC'`: Formats the date and time in the UTC time zone.
 *
 * @param {string|number|undefined} date - The date to format. It can be a date string, a unix timestamp (number), or a unix timestamp as string. If undefined, null, 0, or '0' is provided, returns 'N/A'.
 * @returns {string} - The formatted date string. Returns 'N/A' for undefined/null/0/'0' values. If the input is invalid, the original input is returned.
 *
 * @example
 * formatDateTime('2025-06-27T15:30:00Z');
 * Output: "Jun 27, 2025 at 3:30:00 PM UTC"
 *
 * @example
 * formatDateTime(1751046600);
 * Output: "Jun 27, 2025 at 3:30:00 PM UTC"
 *
 * @example
 * formatDateTime('1751046600');
 * Output: "Jun 27, 2025 at 3:30:00 PM UTC"
 *
 * @example
 * formatDateTime(undefined);
 * Output: "N/A"
 *
 * @example
 * formatDateTime(0);
 * Output: "N/A"
 *
 * @example
 * formatDateTime('invalid-date');
 * Output: "invalid-date"
 */
export function formatDateTime(date?: string | number) {
  if (!date || date === 0 || date === '0') {
    return 'N/A'
  }

  try {
    let parsedDate: Date

    let dateCopy = date
    if (typeof date === 'string' && /^\d+$/.test(date)) {
      dateCopy = Number(date)
    }

    if (typeof dateCopy === 'number') {
      // Check if the number is in seconds or milliseconds
      parsedDate =
        dateCopy > TIMESTAMP_THRESHOLD
          ? new Date(dateCopy)
          : new Date(dateCopy * 1000)
    } else {
      parsedDate = new Date(dateCopy)
    }

    // Explicitly check for invalid dates before formatting
    if (Number.isNaN(parsedDate.getTime())) {
      return String(date)
    }

    return new Intl.DateTimeFormat('en-US', {
      timeStyle: 'long',
      dateStyle: 'medium',
      timeZone: 'UTC',
    }).format(parsedDate)
  } catch (_error) {
    // If any other error occurs during parsing or formatting, display the date as is
    return String(date)
  }
}

/**
 * Formats a date into a human-readable string that indicates the time elapsed since the date.
 * @param {string | number} date - A date string or a Unix timestamp (in seconds or milliseconds).
 * @returns {string} - A human-readable string representing the time elapsed since the given date, e.g., "3 days ago", "in 2 hours", etc.
 */
export function timeAgo(date: string | number): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true })
}
